import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';

interface PublishParams {
  id: string;
  actorId?: string | null;
}

/**
 * POST /api/ConsentTemplate/Publish/{id}?actorId= → boolean.
 *
 * Contract: Scrips.Patient `ConsentTemplateController.Publish` (PR #882).
 * Activates a draft and bumps the version line. No body — actorId is a query
 * param (matches the controller `[FromQuery] Guid? actorId`).
 *
 * actorId is AUDIT-ONLY (stored as Updatedby) — NOT a trust boundary (Samer
 * review #550). Authorization is `[Authorize]` + the org header; a caller can't
 * escalate by passing someone else's actorId. Hardening follow-up: the BE should
 * derive the authoritative actor from the JWT/UserId header rather than trust
 * this client-supplied param (apiClient already sends UserId). Tracked for #882.
 */
export function usePublishConsentTemplate() {
  const qc = useQueryClient();
  return useMutation<boolean, Error, PublishParams>({
    mutationFn: ({ id, actorId }) => {
      const qs = actorId ? `?actorId=${encodeURIComponent(actorId)}` : '';
      return post<boolean>(`/api/ConsentTemplate/Publish/${id}${qs}`, {});
    },
    onSuccess: (_ok, { id }) => {
      invalidateIfNotInFlight(qc, ['consent-templates']);
      invalidateIfNotInFlight(qc, ['consent-template', id]);
    },
  });
}
